import { useState } from 'react';
import { UploadStatus, type UploadProgress } from 'src/types/UserDocuments';

/**
 * The per-file progress rows an upload hook shows under its dropzone.
 * `track` adds a row for a file, runs its upload, and marks the row uploaded
 * (with the stored document's id) or failed (with the error). It never
 * throws: a failure resolves to null and stays visible on its row.
 */
export function useUploadProgressRows() {
  const [progress, setProgress] = useState<UploadProgress[]>([]);

  function patch(rowId: string, changes: Partial<UploadProgress>) {
    setProgress((rows) =>
      rows.map((row) =>
        row.documentId === rowId ? { ...row, ...changes } : row,
      ),
    );
  }

  /** `upload` resolves to the stored document's id; `setPercent` moves the bar. */
  async function track(
    file: File,
    upload: (setPercent: (percent: number) => void) => Promise<string>,
  ): Promise<string | null> {
    // Temporary id keys the row until the real documentId comes back.
    const rowId = crypto.randomUUID();
    setProgress((rows) => [
      ...rows,
      {
        documentId: rowId,
        fileName: file.name,
        progress: 0,
        status: UploadStatus.uploading,
      },
    ]);
    try {
      const documentId = await upload((percent) =>
        patch(rowId, { progress: percent }),
      );
      patch(rowId, {
        documentId,
        progress: 100,
        status: UploadStatus.uploaded,
      });
      return documentId;
    } catch (error) {
      patch(rowId, {
        status: UploadStatus.failed,
        error: error instanceof Error ? error.message : 'Upload failed',
      });
      return null;
    }
  }

  return { progress, track, reset: () => setProgress([]) };
}
